import { useEffect, useMemo, useState } from 'react';
import type {
  ApiClient, Elektroverteiler, Elektrostromkreis,
} from '../api';
import { IconOk, IconWarnung } from '../Icons';
import { VerteilerNachweisdaten, KreisNachweisdaten }
  from './ElektroNachweisdaten';

/**
 * Die Abgänge eines Verteilers verwalten.
 *
 * --- Der Aufbau ------------------------------------------------------------------
 *
 * Oben die vorhandenen Abgänge, darunter „Abgang nicht zugeordnet" mit den Bauteilen,
 * die am Verteiler hängen, aber keinem Kreis.
 *
 * --- Zwei Wege -------------------------------------------------------------------
 *
 *   automatisch bilden      wendet die Regeln aus elektro_kreisregeln an
 *   neuen Abgang erstellen  aus der Auswahl in der Liste
 *
 * Der erste ist ein Vorschlag nach üblichen Mustern und fachlich zu prüfen. Der
 * zweite ist Handarbeit und wird als solche vermerkt — eine von Hand gesetzte
 * Zuordnung wird beim nächsten Auslesen nicht überschrieben.
 *
 * --- Warum die Auswahl je Raum geschieht -----------------------------------------
 *
 * Ein Kreis nimmt ganze Räume. Wer zwanzig Leuchten einzeln anhaken muss, macht es
 * nicht — also wird je Raum und Bauteilart gewählt, nicht je Stück.
 */
export function ElektroAbgaenge({
  api, verteiler, geschlossen,
}: {
  api: ApiClient;
  verteiler: Elektroverteiler;
  geschlossen: () => void;
}) {
  const [kreise, setKreise] = useState<Elektrostromkreis[] | null>(null);
  const [offen, setOffen] = useState<Awaited<
    ReturnType<ApiClient['elektroNichtZugeordnet']>> | null>(null);

  const [gewaehlt, setGewaehlt] = useState<Set<string>>(new Set());
  const [laeuft, setLaeuft] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [probe, setProbe] = useState<string[] | null>(null);
  const [aendern, setAendern] = useState<string | null>(null);

  // Die Prüfliste. Sie bleibt stehen, bis sie geschlossen wird — sie ist die
  // Grundlage der Entscheidung, ob ein Plan ausgegeben werden darf.
  const [pruefliste, setPruefliste] = useState<Awaited<
    ReturnType<ApiClient['elektroPlandatenPruefen']>> | null>(null);

  // Die Vorschlagswerte. Sie bleiben stehen, bis übernommen oder verworfen wird —
  // wer 40 Werte übernimmt, sollte sie vorher gesehen haben.
  const [vorschlag, setVorschlag] = useState<Awaited<
    ReturnType<ApiClient['elektroVorschlag']>> | null>(null);

  // Welcher Abgang gerade in der Pflegemaske steht, und ob die Verteilermaske
  // offen ist.
  const [pflege, setPflege] = useState<string | null>(null);
  const [verteilerPflege, setVerteilerPflege] = useState(false);

  const laden = async () => {
    try {
      const [k, o] = await Promise.all([
        api.elektroStromkreise(verteiler.id),
        api.elektroNichtZugeordnet(verteiler.id),
      ]);

      setKreise(k);
      setOffen(o);
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => { laden(); }, [verteiler.id]);

  // Die gewählten Kennungen aus der Auswahl je Raum und Art.
  const gewaehlteIds = useMemo(() => {
    const aus: string[] = [];

    for (const raum of offen?.jeRaum ?? []) {
      for (const art of raum.arten) {
        if (gewaehlt.has(`${raum.raumnummer ?? ''}|${art.bauteilart}`))
          aus.push(...art.ids);
      }
    }

    return aus;
  }, [gewaehlt, offen]);

  const umschalten = (schluessel: string) => {
    const n = new Set(gewaehlt);
    n.has(schluessel) ? n.delete(schluessel) : n.add(schluessel);
    setGewaehlt(n);
  };

  const ganzenRaum = (raumnummer: string | null, arten: string[]) => {
    const n = new Set(gewaehlt);
    const schluessel = arten.map((a) => `${raumnummer ?? ''}|${a}`);
    const alleDrin = schluessel.every((s) => n.has(s));

    schluessel.forEach((s) => alleDrin ? n.delete(s) : n.add(s));
    setGewaehlt(n);
  };

  // --- Automatisch bilden --------------------------------------------------

  const bilden = async (nurPruefen: boolean) => {
    setLaeuft(true);
    setProbe(null);

    try {
      const a = await api.elektroAbgaengeBilden(verteiler.id, nurPruefen);

      // schicke() gibt void zurueck, wenn die Antwort keinen Koerper hat. Der
      // Uebersetzer verlangt die Pruefung - und sie ist richtig: Ohne Antwort gibt
      // es nichts anzuzeigen.
      if (!a) { setMeldung('Keine Antwort von der Schnittstelle.'); return; }

      if (nurPruefen) {
        // Die Probe bleibt stehen, nicht in einer Meldung, die man wegklickt —
        // sie ist die Grundlage der Entscheidung.
        setProbe([
          a.message,
          `${a.teVorhanden} + ${a.teNeu} Teilungseinheiten, mit Hauptschalter `
          + `und Reserve ${a.teBedarf} bei ${a.teJeFeld} je Feld.`,
          ...a.hinweise,
          ...a.kreise.slice(0, 12).map((k) =>
            `  ${k.verwendung}  ${k.schutzorgan ?? ''}  ${k.raeume}  `
            + `${k.bauteile} Bauteil(e), ${k.te} TE`),
          ...(a.kreise.length > 12
            ? [`  … und ${a.kreise.length - 12} weitere`] : []),
        ]);
      } else {
        setMeldung(a.message
          + (a.zusaetzlicheFelder > 0
            ? ` — ${a.zusaetzlicheFelder} zusätzliches Verteilerfeld erforderlich.`
            : ''));

        setGewaehlt(new Set());
        await laden();
      }
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  // --- Von Hand einen Abgang anlegen ---------------------------------------

  const neuerAbgang = async () => {
    if (gewaehlteIds.length === 0) return;

    const kennzeichen = prompt(
      `Kennzeichen des neuen Abgangs (z. B. F12):\n\n`
      + `${gewaehlteIds.length} Bauteil(e) werden zugewiesen.`);

    if (!kennzeichen?.trim()) return;

    setLaeuft(true);
    try {
      const angelegt = await api.elektroStromkreisAnlegen(verteiler.id, {
        kennzeichen: kennzeichen.trim(),
        bezeichnung: null,
        verwendung: null,
        schutzorgan: null,
        bemessungsstrom: null,
        polzahl: null,
        querschnitt: null,
        leitungsart: null,
        laenge: null,
        istSicherheit: false,
        hinweis: 'Von Hand angelegt.',
      });

      if (angelegt?.id) {
        const a = await api.elektroKreisMengeSetzen(angelegt.id, gewaehlteIds);
        setMeldung(a ? a.message : 'Angelegt.');
      }

      setGewaehlt(new Set());
      await laden();
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  // --- Einen bestehenden Abgang ändern -------------------------------------

  const mengeSetzen = async (kreisId: string) => {
    setLaeuft(true);
    try {
      const a = await api.elektroKreisMengeSetzen(kreisId, gewaehlteIds);

      setMeldung(a ? a.message : 'Gesetzt.');
      setGewaehlt(new Set());
      setAendern(null);
      await laden();
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  const entfernen = async (k: Elektrostromkreis) => {
    if (!confirm(
      `Abgang '${k.kennzeichen}' löschen?\n\n`
      + `${k.bauteile ?? 0} Bauteil(e) verlieren ihre Zuordnung und erscheinen `
      + `wieder unter "Abgang nicht zugeordnet".`
    )) return;

    setLaeuft(true);
    try {
      const a = await api.elektroStromkreisEntfernen(k.id);
      setMeldung(a ? a.message : 'Gelöscht.');
      await laden();
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  if (!kreise || !offen) return <div className="leer">wird geladen …</div>;

  const teBelegt = kreise.reduce((n, k) => n + (k.teilungseinheiten ?? 1), 0);

  return (
    <div style={{ padding: '10px 0' }}>
      {meldung && (
        <div className="hinweis" style={{ marginBottom: 10 }}>
          {meldung}
          <button onClick={() => setMeldung(null)}
                  style={{ marginLeft: 8, padding: '1px 6px', fontSize: 11 }}>
            schließen
          </button>
        </div>
      )}

      {/* --- Die Werkzeuge ------------------------------------------------- */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap',
                    alignItems: 'center', marginBottom: 12 }}>
        <button onClick={() => bilden(true)} disabled={laeuft}
                style={{ fontSize: 12 }}
                title="Rechnen, ohne anzulegen">
          prüfen
        </button>

        <button onClick={() => bilden(false)} disabled={laeuft}
                className="betont" style={{ fontSize: 12 }}
                title="Abgänge nach den Regeln in elektro_kreisregeln bilden">
          {laeuft ? 'läuft …' : 'automatisch bilden'}
        </button>

        <span style={{ width: 12 }} />

        <button onClick={neuerAbgang}
                disabled={laeuft || gewaehlteIds.length === 0}
                style={{ fontSize: 12 }}>
          neuen Abgang erstellen
          {gewaehlteIds.length > 0 && ` (${gewaehlteIds.length})`}
        </button>

        {gewaehlt.size > 0 && (
          <button onClick={() => setGewaehlt(new Set())}
                  style={{ fontSize: 11, padding: '2px 7px' }}>
            Auswahl aufheben
          </button>
        )}

        <button onClick={() => setVerteilerPflege(!verteilerPflege)}
                style={{ fontSize: 12 }}
                title="Netzform, Kurzschlussströme, Schranksystem">
          Verteilerdaten
          {verteiler.nachweisHerkunft === 'vorschlag' && (
            <span className="marke warnung" style={{ fontSize: 9, marginLeft: 4 }}
                  title="aus dem Vorschlagslauf, nicht geprüft">V</span>
          )}
        </button>

        <button
          onClick={async () => {
            setLaeuft(true);
            setProbe(null);
            try {
              const a = await api.elektroLaengenErmitteln(verteiler.id, false);

              if (!a) { setMeldung('Keine Antwort.'); return; }

              setProbe([a.message, ...a.hinweise]);
              await laden();
            } catch (e) {
              setMeldung(e instanceof Error ? e.message : String(e));
            } finally {
              setLaeuft(false);
            }
          }}
          disabled={laeuft || kreise.length === 0}
          style={{ fontSize: 12 }}
          title="Trassenlängen aus den Bauteilkoordinaten — Manhattan plus 15 %"
        >
          Längen ermitteln
        </button>

        <button
          onClick={async () => {
            setLaeuft(true);
            setProbe(null);
            try {
              const a = await api.elektroLeistungSummieren(verteiler.id, false);

              if (!a) { setMeldung('Keine Antwort.'); return; }

              // Die Hinweise bleiben stehen: Ein Abgang, bei dem die Hälfte der
              // Bauteile keinen Wert trägt, hat eine Summe, die aussieht wie eine
              // Summe.
              setProbe([a.message, ...a.hinweise]);
              await laden();
            } catch (e) {
              setMeldung(e instanceof Error ? e.message : String(e));
            } finally {
              setLaeuft(false);
            }
          }}
          disabled={laeuft || kreise.length === 0}
          style={{ fontSize: 12 }}
          title="Die Anschlussleistung der Bauteile je Abgang summieren"
        >
          Leistung summieren
        </button>

        <button
          onClick={async () => {
            setLaeuft(true);
            try {
              setVorschlag(await api.elektroVorschlag(verteiler.id));
            } catch (e) {
              setMeldung(e instanceof Error ? e.message : String(e));
            } finally {
              setLaeuft(false);
            }
          }}
          disabled={laeuft || kreise.length === 0}
          style={{ fontSize: 12 }}
          title="Übliche Ansätze vorschlagen — ohne zu schreiben"
        >
          Nachweisdaten vorschlagen
        </button>

        <button
          onClick={async () => {
            setLaeuft(true);
            try {
              setPruefliste(await api.elektroPlandatenPruefen(verteiler.id));
            } catch (e) {
              setMeldung(e instanceof Error ? e.message : String(e));
            } finally {
              setLaeuft(false);
            }
          }}
          disabled={laeuft || kreise.length === 0}
          style={{ fontSize: 12 }}
          title="Die Nachweise nach DIN VDE 0100 rechnen und die Prüfliste zeigen"
        >
          Plandaten prüfen
        </button>

        <button
          onClick={async () => {
            setLaeuft(true);
            try {
              await api.elektroVerteilerplanLaden(
                verteiler.id, verteiler.kennzeichen);
            } catch (e) {
              setMeldung(e instanceof Error ? e.message : String(e));
            } finally {
              setLaeuft(false);
            }
          }}
          disabled={laeuft || kreise.length === 0}
          style={{ fontSize: 12 }}
          title={pruefliste && !pruefliste.freigabefaehig
            ? 'Der Plan trägt den Vorabzugsvermerk, solange Nachweise fehlen'
            : 'Einpoliger Stromlaufplan und Stromkreisliste als A3-PDF'}
        >
          Verteilerplan erstellen
        </button>

        <span style={{ flex: 1 }} />

        <span className="sehr-schwach" style={{ fontSize: 11 }}>
          {teBelegt} von {verteiler.teJeFeld ?? 48} TE belegt
          {verteiler.felderBedarf && verteiler.felderBedarf > (verteiler.felder ?? 1)
            ? ` · ${verteiler.felderBedarf - (verteiler.felder ?? 1)} Feld(er) fehlen`
            : ''}
        </span>
      </div>

      {/* --- Die Verteilerdaten ------------------------------------------- */}
      {verteilerPflege && (
        <div className="karte" style={{ marginBottom: 12 }}>
          <VerteilerNachweisdaten
            api={api}
            verteiler={verteiler}
            fertig={(m) => { setMeldung(m); setVerteilerPflege(false); laden(); }}
          />
        </div>
      )}

      {/* --- Die Vorschlagswerte ------------------------------------------ */}
      {vorschlag && (
        <div className="karte" style={{ marginBottom: 12,
          borderLeft: '3px solid var(--warnung, #d97706)' }}>
          <div style={{ fontSize: 12, marginBottom: 8 }}>
            {vorschlag.message}
          </div>

          {vorschlag.verteiler.length > 0 && (
            <>
              <div className="feldname" style={{ marginBottom: 4 }}>
                Am Verteiler
              </div>

              <div className="tabellenrahmen" style={{ marginBottom: 10 }}>
                <table className="zuordnung" style={{ width: '100%' }}>
                  <tbody>
                    {vorschlag.verteiler.map((w) => (
                      <tr key={w.feld}>
                        <td style={{ width: 200, fontSize: 11 }}>
                          {w.bezeichnung}
                        </td>
                        <td style={{ width: 70, fontSize: 12 }}>
                          <strong>{w.neu}</strong>
                        </td>
                        <td className="schwach" style={{ fontSize: 10 }}>
                          {w.begruendung}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {vorschlag.kreise.length > 0 && (
            <>
              <div className="feldname" style={{ marginBottom: 4 }}>
                An {vorschlag.kreise.length} Abgang/Abgängen
              </div>

              {/* Nur der erste Abgang je Verwendung ausführlich — die übrigen
                  bekommen dieselben Werte, und vierzig gleiche Blöcke wären
                  unlesbar. */}
              <div className="tabellenrahmen" style={{ maxHeight: 300,
                                                       overflowY: 'auto',
                                                       marginBottom: 8 }}>
                <table className="zuordnung" style={{ width: '100%' }}>
                  <tbody>
                    {Array.from(new Map(
                      vorschlag.kreise.map((k) => [k.verwendung ?? '', k])
                    ).values()).map((k) => (
                      <tr key={k.id}>
                        <td style={{ width: 110, fontSize: 11,
                                     verticalAlign: 'top' }}>
                          {k.verwendung ?? '(ohne)'}
                          <div className="sehr-schwach" style={{ fontSize: 10 }}>
                            z. B. {k.kennzeichen}
                          </div>
                        </td>

                        <td>
                          <div style={{ display: 'flex', gap: 6,
                                        flexWrap: 'wrap' }}>
                            {k.werte.map((w) => (
                              <span key={w.feld} style={{ fontSize: 10 }}
                                    title={w.begruendung}>
                                {w.bezeichnung} <strong>{w.neu}</strong>
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="sehr-schwach" style={{ fontSize: 10,
                                                     marginBottom: 8 }}>
                Je Verwendung ein Beispiel — die übrigen Abgänge derselben
                Verwendung bekommen dieselben Werte. Die Begründung steht als
                Hinweis am Wert.
              </div>
            </>
          )}

          {vorschlag.offen.length > 0 && (
            <div className="hinweis" style={{ marginBottom: 8 }}>
              <IconWarnung size={13} />
              <div style={{ fontSize: 11 }}>
                <strong>Dazu wird bewusst nichts vorgeschlagen:</strong>
                {vorschlag.offen.map((o, i) => (
                  <div key={i} style={{ marginTop: 4 }}>{o}</div>
                ))}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              onClick={async () => {
                if (!confirm(
                  `${vorschlag.anzahl} Vorschlagswert(e) übernehmen?\n\n`
                  + 'Sie werden als „vorschlag" vermerkt. Das Qualitätsgate '
                  + 'meldet sie als WARNUNG, und jeder Plan trägt weiterhin den '
                  + 'Vorabzugsvermerk, bis sie fachlich geprüft sind.'
                )) return;

                setLaeuft(true);
                try {
                  const a = await api.elektroVorschlagUebernehmen(verteiler.id);
                  setMeldung(a ? a.message : 'Übernommen.');
                  setVorschlag(null);
                  await laden();
                } catch (e) {
                  setMeldung(e instanceof Error ? e.message : String(e));
                } finally {
                  setLaeuft(false);
                }
              }}
              disabled={laeuft || vorschlag.anzahl === 0}
              className="betont"
              style={{ fontSize: 12 }}
            >
              {laeuft ? 'läuft …' : `alle ${vorschlag.anzahl} übernehmen`}
            </button>

            <button onClick={() => setVorschlag(null)} style={{ fontSize: 12 }}>
              verwerfen
            </button>

            <span className="sehr-schwach" style={{ fontSize: 10 }}>
              Gepflegte Werte werden nicht überschrieben.
            </span>
          </div>
        </div>
      )}

      {/* --- Die Prüfliste ------------------------------------------------ */}
      {pruefliste && (
        <div className="karte" style={{ marginBottom: 12,
          borderLeft: `3px solid ${pruefliste.freigabefaehig
            ? 'var(--gruen, #16a34a)' : 'var(--warnung, #d97706)'}` }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
            <strong style={{ fontSize: 13 }}>{pruefliste.vermerk}</strong>
            <span style={{ flex: 1 }} />
            <span className="sehr-schwach" style={{ fontSize: 10 }}>
              Prüfhash {pruefliste.pruefhash}
            </span>
            <button onClick={() => setPruefliste(null)}
                    style={{ padding: '1px 6px', fontSize: 11 }}>
              schließen
            </button>
          </div>

          <div style={{ display: 'flex', gap: 12, margin: '8px 0',
                        fontSize: 11, flexWrap: 'wrap' }}>
            {([
              ['BLOCKER', pruefliste.zusammenfassung.blocker],
              ['FEHLER', pruefliste.zusammenfassung.fehler],
              ['WARNUNG', pruefliste.zusammenfassung.warnungen],
              ['HINWEIS', pruefliste.zusammenfassung.hinweise],
            ] as [string, number][]).filter(([, n]) => n > 0).map(([s2, n]) => (
              <span key={s2}>
                <strong>{n}</strong> {s2}
              </span>
            ))}
          </div>

          <div className="tabellenrahmen" style={{ maxHeight: 340,
                                                   overflowY: 'auto' }}>
            <table className="zuordnung" style={{ width: '100%' }}>
              <tbody>
                {pruefliste.befunde.map((b, i) => (
                  <tr key={i}>
                    <td style={{ width: 74, fontSize: 10, verticalAlign: 'top' }}>
                      <span className={b.stufe === 'BLOCKER'
                                    || b.stufe === 'FEHLER'
                        ? 'marke warnung' : undefined}
                            style={{ fontSize: 9 }}>
                        {b.stufe}
                      </span>
                    </td>

                    <td style={{ width: 46, fontSize: 10,
                                 verticalAlign: 'top' }}
                        className="sehr-schwach">
                      {b.regelId}
                    </td>

                    <td style={{ width: 90, fontSize: 11,
                                 verticalAlign: 'top' }}>
                      {b.gegenstand}
                    </td>

                    <td style={{ fontSize: 11 }}>
                      {b.meldung}
                      {b.normbezug && (
                        <div className="sehr-schwach" style={{ fontSize: 10 }}>
                          {b.normbezug}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!pruefliste.freigabefaehig && (
            <div style={{ fontSize: 11, marginTop: 6 }}>
              <strong>Ein jetzt erzeugter Verteilerplan trägt auf jeder Seite
              diagonal „VORABZUG — NICHT ZUR AUSFÜHRUNG".</strong>
              {' '}Er ist dennoch nützlich: Er zeigt, wie der Plan aussehen wird,
              und macht die Lücken sichtbar.
            </div>
          )}

          <div className="sehr-schwach" style={{ fontSize: 10, marginTop: 6 }}>
            Fehlende Eingaben werden als BLOCKER gemeldet und <strong>nicht</strong>
            {' '}durch Annahmen ersetzt. Eine Rechnung mit erfundenen Eingaben sieht
            aus wie ein Nachweis, ist aber keiner.
          </div>
        </div>
      )}

      {probe && (
        <div className="hinweis" style={{ marginBottom: 12 }}>
          <IconWarnung size={13} />
          <div style={{ fontSize: 12, whiteSpace: 'pre-line' }}>
            {probe.join('\n')}
          </div>
          <button onClick={() => setProbe(null)}
                  style={{ marginTop: 6, padding: '1px 7px', fontSize: 11 }}>
            schließen
          </button>
        </div>
      )}

      {/* --- Die vorhandenen Abgänge -------------------------------------- */}
      {kreise.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div className="feldname" style={{ marginBottom: 4 }}>
            {kreise.length} Abgang/Abgänge
          </div>

          <div className="tabellenrahmen">
            <table className="zuordnung" style={{ width: '100%' }}>
              <tbody>
                {kreise.map((k) => (
                  <tr key={k.id}>
                    <td style={{ fontSize: 12, width: 70 }}>
                      {k.kennzeichen}
                      {k.istSicherheit && (
                        <span className="marke warnung"
                              style={{ fontSize: 9, marginLeft: 4 }}
                              title="Sicherheitsstromversorgung">SV</span>
                      )}
                      {/*
                        Ein Abgang der Gebäudeautomation. Er wird bei jedem
                        Ableitungslauf aus msr_leistungsbilanz nachgezogen —
                        wer ihn hier von Hand ändert, hat die Änderung nach
                        dem nächsten Lauf nicht mehr.
                      */}
                      {k.herkunft === 'msr' && (
                        <span className="marke"
                              style={{ fontSize: 9, marginLeft: 4 }}
                              title="Automationsschrank der Gebäudeautomation — aus msr_leistungsbilanz gerechnet, wird bei jedem Ableitungslauf nachgezogen">MSR</span>
                      )}
                    </td>

                    <td className="schwach" style={{ fontSize: 11 }}>
                      {k.bezeichnung ?? k.verwendung ?? '—'}
                    </td>

                    <td className="sehr-schwach"
                        style={{ fontSize: 11, width: 80 }}>
                      {k.schutzorgan ?? '—'}
                    </td>

                    <td className="sehr-schwach"
                        style={{ fontSize: 11, width: 46, textAlign: 'right' }}>
                      {k.teilungseinheiten ?? 1} TE
                    </td>

                    <td className="schwach"
                        style={{ fontSize: 11, width: 60, textAlign: 'right' }}>
                      {k.bauteile ?? 0}
                    </td>

                    <td style={{ width: 150, textAlign: 'right' }}>
                      {aendern === k.id ? (
                        <>
                          <button onClick={() => mengeSetzen(k.id)}
                                  disabled={laeuft}
                                  className="betont"
                                  style={{ fontSize: 10, padding: '1px 6px' }}
                                  title="Die Auswahl wird die neue Menge — was fehlt, wird gelöst">
                            Auswahl setzen ({gewaehlteIds.length})
                          </button>

                          <button onClick={() => setAendern(null)}
                                  style={{ fontSize: 10, padding: '1px 6px',
                                           marginLeft: 4 }}>
                            ab
                          </button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => setPflege(
                                    pflege === k.id ? null : k.id)}
                                  disabled={laeuft}
                                  style={{ fontSize: 10, padding: '1px 6px' }}
                                  title="Nachweisdaten pflegen">
                            Daten
                            {k.nachweisHerkunft === 'vorschlag' && (
                              <span style={{ marginLeft: 2 }}
                                    title="aus dem Vorschlagslauf">V</span>
                            )}
                          </button>

                          <button onClick={() => setAendern(k.id)}
                                  disabled={laeuft}
                                  style={{ fontSize: 10, padding: '1px 6px',
                                           marginLeft: 4 }}
                                  title="Bauteile an- oder abwählen">
                            Bauteile
                          </button>

                          <button onClick={() => entfernen(k)} disabled={laeuft}
                                  style={{ fontSize: 10, padding: '1px 6px',
                                           marginLeft: 4 }}>
                            löschen
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}

                {/* Die Pflegemaske als eigene Zeile unter dem Abgang — nicht als
                    Dialog: Wer Werte einträgt, will die Nachbarzeilen sehen. */}
                {pflege && kreise.some((k) => k.id === pflege) && (
                  <tr>
                    <td colSpan={6} style={{ background: 'var(--flaeche, #fafafa)',
                                             padding: '4px 12px' }}>
                      <KreisNachweisdaten
                        api={api}
                        kreis={kreise.find((k) => k.id === pflege)!}
                        fertig={(m) => { setMeldung(m); laden(); }}
                        geschlossen={() => setPflege(null)}
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {aendern && (
            <div className="sehr-schwach" style={{ fontSize: 11, marginTop: 4 }}>
              Wählen Sie unten die Räume und Arten, die an diesem Abgang hängen
              sollen. <strong>Was nicht gewählt ist, wird gelöst</strong> — auch was
              bisher daran hing.
            </div>
          )}
        </div>
      )}

      {/* --- Abgang nicht zugeordnet -------------------------------------- */}
      <div className="feldname" style={{ marginBottom: 4 }}>
        Abgang nicht zugeordnet
        <span className="schwach" style={{ marginLeft: 6 }}>
          {offen.gesamt.toLocaleString('de-DE')} Bauteil(e) in {offen.raeume} Raum/Räumen
        </span>
      </div>

      {offen.gesamt === 0 ? (
        <div className="leer" style={{ fontSize: 12 }}>
          <IconOk size={13} /> Alle Bauteile dieses Verteilers hängen an einem Abgang.
        </div>
      ) : (
        <div className="tabellenrahmen" style={{ maxHeight: 420,
                                                 overflowY: 'auto' }}>
          <table className="zuordnung" style={{ width: '100%' }}>
            <tbody>
              {offen.jeRaum.map((raum) => {
                const schluessel = raum.arten.map((a) => a.bauteilart);

                const alleDrin = schluessel.every((a) =>
                  gewaehlt.has(`${raum.raumnummer ?? ''}|${a}`));

                return (
                  <tr key={raum.raumnummer ?? 'ohne'}>
                    <td style={{ width: 22 }}>
                      <input type="checkbox" checked={alleDrin}
                             onChange={() =>
                               ganzenRaum(raum.raumnummer, schluessel)} />
                    </td>

                    <td style={{ fontSize: 12, width: 90 }}>
                      {raum.raumnummer ?? (
                        <span className="sehr-schwach">ohne Raum</span>
                      )}
                    </td>

                    <td className="schwach" style={{ fontSize: 11 }}>
                      {raum.raumname ?? '—'}
                    </td>

                    <td>
                      {/* Je Art einzeln wählbar — Beleuchtung und Steckdosen
                          gehören nicht auf einen Kreis, und wer das trennen will,
                          muss es hier tun können. */}
                      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                        {raum.arten.map((art) => {
                          const s = `${raum.raumnummer ?? ''}|${art.bauteilart}`;
                          const drin = gewaehlt.has(s);

                          return (
                            <button
                              key={art.bauteilart}
                              onClick={() => umschalten(s)}
                              style={{
                                fontSize: 10, padding: '1px 6px',
                                background: drin
                                  ? 'var(--betont, #e0e7ff)' : undefined,
                                fontWeight: drin ? 500 : 400,
                              }}
                              title={`Kostengruppe ${art.kostengruppe ?? '—'}`}
                            >
                              {art.bezeichnung} {art.anzahl}
                            </button>
                          );
                        })}
                      </div>
                    </td>

                    <td className="schwach"
                        style={{ fontSize: 11, width: 50, textAlign: 'right' }}>
                      {raum.anzahl}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
        <button onClick={geschlossen} style={{ fontSize: 12 }}>
          schließen
        </button>

        <span className="sehr-schwach" style={{ fontSize: 11, alignSelf: 'center' }}>
          Die automatische Bildung folgt üblichen Mustern und ist fachlich zu prüfen.
        </span>
      </div>
    </div>
  );
}
